import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  Mail,
  Megaphone,
  MessageCircle,
  Pencil,
  Plus,
  Send,
} from 'lucide-react'
import { MarketingShell } from './MarketingShell'
import { CampaignDrawer } from './CampaignDrawer'
import { WhatsAppDispatchModal } from './WhatsAppDispatchModal'
import { WAAllowanceMeter } from './WAAllowanceMeter'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency } from '@/lib/utils'
import type { APIResponse, Campaign } from '@/types'
import { describeApiError } from '@/lib/apiError'

const OCCASION_EMOJI: Record<string, string> = {
  ramadan: '🌙',
  eid: '✨',
  independence: '🇵🇰',
  psl: '🏏',
  winter: '☕',
  general: '💛',
}

/**
 * The Pakistani-calendar preset gallery — there is always a ready-to-send
 * idea in season, so the tab never feels empty. "Use this" creates a draft
 * and drops the owner into the normal editor.
 */
function PresetGallery() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const presetsQuery = useQuery({
    queryKey: ['marketing', 'campaign-presets'],
    queryFn: () => apiClient.listCampaignPresets(),
    staleTime: Infinity,
  })
  const presets = presetsQuery.data?.data?.presets ?? []

  const use = useMutation({
    mutationFn: (key: string) => apiClient.useCampaignPreset(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      toast({
        title: 'Draft created',
        description: 'The preset is now a draft campaign below — edit and send when ready.',
      })
    },
    onError: () => toast({ title: 'Could not create the draft', variant: 'destructive' }),
  })

  if (presets.length === 0) return null
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">
        Start from a template
      </h3>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {presets.map((p) => (
          <div
            key={p.key}
            className="w-56 shrink-0 rounded-lg border border-border/80 bg-background p-3 shadow-sm"
          >
            <div className="text-lg">{OCCASION_EMOJI[p.occasion] ?? '📣'}</div>
            <div className="mt-1 text-sm font-semibold">{p.title}</div>
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {p.description}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">{p.suggested_window}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2 h-7 w-full text-xs"
              onClick={() => use.mutate(p.key)}
              disabled={use.isPending}
            >
              Use this
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  archived: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
}

const SEGMENT_LABEL: Record<string, string> = {
  birthdays_this_month: 'Birthdays this month',
  lapsed_60d: 'Lapsed (60+ days)',
  at_risk: 'At risk',
  vips: 'VIPs',
  first_timers_recent: 'First-timers',
  regulars: 'Regulars',
}

/**
 * Campaigns manager. Create/edit via CampaignDrawer; three send paths per
 * campaign:
 *   - Email — server-side enqueue through the outbox
 *   - WhatsApp (click) — the manager's own device opens wa.me per guest
 *   - WhatsApp (auto) — server-side template sends through the Cloud API,
 *     consent-gated and metered; a 409 from the allowance meter surfaces
 *     with the exact counts instead of a partial send
 */
export function AdminCampaigns() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Campaign | null>(null)
  const [waClickCampaign, setWaClickCampaign] = useState<Campaign | null>(null)
  const [confirmSend, setConfirmSend] = useState<{
    campaign: Campaign
    channel: 'email' | 'whatsapp_api'
  } | null>(null)

  const campaignsQuery = useQuery<APIResponse<Campaign[]>>({
    queryKey: ['campaigns'],
    queryFn: () => apiClient.listCampaigns(),
    staleTime: 30_000,
  })
  const campaigns = campaignsQuery.data?.data ?? []

  const dispatchMutation = useMutation({
    mutationFn: ({ id, channel }: { id: string; channel: 'email' | 'whatsapp_api' }) =>
      apiClient.dispatchCampaign(id, { channel }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['marketing', 'wa-allowance'] })
      setConfirmSend(null)
      toast({
        title: 'Campaign queued',
        description: `${res.data?.queued ?? 0} message${(res.data?.queued ?? 0) === 1 ? '' : 's'} queued${
          res.data?.skipped ? `, ${res.data.skipped} skipped` : ''
        }.`,
      })
    },
    onError: (err: unknown) => {
      const { code, message } = describeApiError(err)
      toast({
        title:
          code === 'wa_allowance_insufficient' || code === 'wa_allowance_exhausted'
            ? 'WhatsApp allowance too low'
            : code === 'mint_code_not_delivered'
              ? 'Add {{promo_code}} to the message first'
              : 'Dispatch failed',
        description: message,
        variant: 'destructive',
      })
      setConfirmSend(null)
    },
  })

  return (
    <MarketingShell
      title="Campaigns"
      description="Segment customers, send a targeted offer, and watch redemptions and revenue land back in the dashboard."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <WAAllowanceMeter />
          <Button
            onClick={() => {
              setEditing(null)
              setDrawerOpen(true)
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            New campaign
          </Button>
        </div>

        <PresetGallery />

        {campaignsQuery.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="space-y-3 rounded-lg border border-dashed py-14 text-center">
            <Megaphone className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
            <h2 className="text-base font-semibold">No campaigns yet</h2>
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              Build a segment, hand it a promo code, and send by email or
              WhatsApp — every redemption walks back into the stats, attached
              to the blast that earned it.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setEditing(null)
                setDrawerOpen(true)
              }}
            >
              Create your first campaign
            </Button>
          </div>
        ) : (
          <ul className="space-y-2">
            {campaigns.map((c) => {
              const seg = (c.segment_def as { segment?: string })?.segment ?? ''
              return (
                <li
                  key={c.id}
                  className="rounded-lg border border-border/80 bg-background p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold">{c.name}</h3>
                        <Badge className={STATUS_BADGE[c.status] ?? ''} variant="outline">
                          {c.status}
                        </Badge>
                        {seg && (
                          <span className="text-xs text-muted-foreground">
                            {SEGMENT_LABEL[seg] ?? seg}
                          </span>
                        )}
                        {c.promo_code && (
                          <span className="font-mono text-xs text-muted-foreground">
                            {c.promo_code}
                          </span>
                        )}
                      </div>
                      {c.description && (
                        <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
                          {c.description}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>
                          Sent <strong className="text-foreground">{c.sent_count}</strong>
                        </span>
                        <span>
                          Clicked <strong className="text-foreground">{c.clicked_count}</strong>
                        </span>
                        <span>
                          Redeemed <strong className="text-foreground">{c.redeemed_count}</strong>
                        </span>
                        <span>
                          Revenue{' '}
                          <strong className="text-foreground">
                            {formatCurrency(c.revenue_attributed_pkr)}
                          </strong>
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8"
                        onClick={() => {
                          setEditing(c)
                          setDrawerOpen(true)
                        }}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
                        Edit
                      </Button>
                      {c.channel_set.includes('email') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          disabled={c.status === 'archived'}
                          onClick={() => setConfirmSend({ campaign: c, channel: 'email' })}
                        >
                          <Mail className="mr-1 h-3.5 w-3.5" aria-hidden />
                          Email
                        </Button>
                      )}
                      {c.channel_set.includes('whatsapp_click') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          disabled={c.status === 'archived'}
                          onClick={() => setWaClickCampaign(c)}
                        >
                          <MessageCircle className="mr-1 h-3.5 w-3.5" aria-hidden />
                          WA click
                        </Button>
                      )}
                      {c.channel_set.includes('whatsapp_api') && (
                        <Button
                          size="sm"
                          className="h-8"
                          disabled={c.status === 'archived' || !c.wa_template_name}
                          title={
                            c.wa_template_name
                              ? undefined
                              : 'Pick a WhatsApp template in Edit first'
                          }
                          onClick={() =>
                            setConfirmSend({ campaign: c, channel: 'whatsapp_api' })
                          }
                        >
                          <Send className="mr-1 h-3.5 w-3.5" aria-hidden />
                          WA auto
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <CampaignDrawer
        mode={editing ? 'edit' : 'create'}
        initial={editing ?? undefined}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
      <WhatsAppDispatchModal
        campaign={waClickCampaign}
        open={waClickCampaign !== null}
        onOpenChange={(o) => {
          if (!o) setWaClickCampaign(null)
        }}
      />

      <Dialog
        open={confirmSend !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmSend(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Send “{confirmSend?.campaign.name}” via{' '}
              {confirmSend?.channel === 'email' ? 'email' : 'WhatsApp'}?
            </DialogTitle>
            <DialogDescription>
              {confirmSend?.channel === 'email'
                ? 'Queues one email per segment member with an email address. Members messaged for this campaign in the last 30 days are handled by the recipient list you previewed.'
                : 'Sends the approved WhatsApp template to every segment member who has opted in and has a phone number. All-or-nothing: if the monthly allowance can’t cover the audience, nothing sends.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmSend(null)}
              disabled={dispatchMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                confirmSend &&
                dispatchMutation.mutate({
                  id: confirmSend.campaign.id,
                  channel: confirmSend.channel,
                })
              }
              disabled={dispatchMutation.isPending}
            >
              {dispatchMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              )}
              Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MarketingShell>
  )
}
